/**
 * useCommandPaletteShortcut — global ⌘K / Ctrl+K keyboard binding.
 *
 * Mount once at the authed route shell so the palette is reachable from
 * every surface (chat, documents, calendar, settings…). The hook owns its
 * own open state so a consumer that just wants a Cmd+K toggle doesn't
 * need to wire a Zustand store for it.
 *
 * Behaviour:
 *   - ⌘K (macOS) / Ctrl+K (Win/Linux) toggles the open state.
 *   - Modifier + K wins even when an input/textarea is focused — the
 *     palette MUST be reachable from inside the composer too (Linear
 *     parity, confirmed by Andrea on the v18 audit).
 *   - SSR-safe: window access guarded behind ``useEffect``.
 *
 * Returns a tuple-style ``{ open, setOpen, toggle, close }`` so the
 * consumer can either mount the palette directly with these handlers OR
 * surface ``toggle`` on a header trigger (palette button on the mini-rail).
 */

import { useCallback, useEffect, useState } from "react";

export interface UseCommandPaletteShortcutOptions {
  /**
   * Optional override for the trigger key. Default ``"k"`` (case-
   * insensitive). Use this if you ever need a second palette bound to a
   * different key — e.g. ``"j"`` for a debug-only "jump to" overlay.
   */
  readonly key?: string;
  /**
   * Optional disable flag — useful when the consumer wants to gate the
   * palette behind a feature flag without unmounting the hook (so the
   * open state survives a flag toggle).
   */
  readonly disabled?: boolean;
}

export interface CommandPaletteController {
  readonly open: boolean;
  /** Direct setter — used by tests + programmatic openers (mini-rail). */
  readonly setOpen: (next: boolean) => void;
  /** Convenience toggle — bound to the rail's palette button. */
  readonly toggle: () => void;
  /** Convenience close — bound to ``CommandPalette.onClose``. */
  readonly close: () => void;
}

export function useCommandPaletteShortcut(
  options: UseCommandPaletteShortcutOptions = {},
): CommandPaletteController {
  const { key = "k", disabled = false } = options;
  const [open, setOpen] = useState(false);

  const toggle = useCallback(() => setOpen((prev) => !prev), []);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (disabled) return;
    const targetKey = key.toLowerCase();
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key.toLowerCase() !== targetKey) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      e.preventDefault();
      setOpen((prev) => !prev);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [key, disabled]);

  return { open, setOpen, toggle, close };
}
